import { Injectable } from '@angular/core';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Config } from '../config';
import { CommonService } from '../services/common.service';
import { ReportsService } from './reports.service';

export interface MultiselectType {
  id: number;
  itemName: string;
  translatedName?: string;
}

export interface DashboardFilter {
  location: string;
  locationName: string;
  defaultValue?: string;
  locationValues: string[];
  transformedValues: MultiselectType[];
  dropwDownSettings: any;
}

@Injectable({ providedIn: 'root' })
export class DashboardService {
  private _filtersLoadedOrRecalculated = new BehaviorSubject<boolean>(false);
  filtersLoadedOrRecalculated$ = this._filtersLoadedOrRecalculated.asObservable();

  private dashboardFilterChanged = new BehaviorSubject<void>(undefined);
  dashboardFilterChanged$ = this.dashboardFilterChanged.asObservable();

  private dashboardFiltersLoadedOrRecalculated = new BehaviorSubject<boolean>(false);
  dashboardFiltersLoadedOrRecalculated$ = this.dashboardFiltersLoadedOrRecalculated.asObservable();

  private dashboardSpecificFiltersChanged = new BehaviorSubject<void>(undefined);
  dashboardSpecificFiltersChanged$ = this.dashboardSpecificFiltersChanged.asObservable();

  activeDashboardFilters: any = {};
  currentDashboardSpecificFilters: DashboardFilter[] = [];
  allAvailableDashboardFilters: any[] = [];

  public constructor(private cService: CommonService, private rptService: ReportsService) {}

  triggerFiltersLoadedOrRecalculated(recalculated: boolean) {
    this._filtersLoadedOrRecalculated.next(recalculated);
  }

  // TODO: Needs translating
  public getDashboardLocationFilters(currentFilter: unknown = {}): Observable<object> {
    return this.cService.commonPost(`/dashboard/filters`, currentFilter).pipe(
      map((response) => {
        if (response.data && response.data.length > 0) {
          return response.data;
        }
      }),
      catchError((err) => {
        this.cService.showToast(':: Failed to load location for this account');
        return throwError(err);
      })
    );
  }

  triggerDashboardFilterChange() {
    this.dashboardFilterChanged.next();
  }

  triggerDashboardLoadedOrRecalculated(recalculated: boolean) {
    this.dashboardFiltersLoadedOrRecalculated.next(recalculated);
  }

  triggerDashboardSpecificFiltersChange() {
    this.dashboardSpecificFiltersChanged.next();
  }

  resetFilters(): void {
    for (const filterKey in this.activeDashboardFilters) {
      if (filterKey) {
        this.activeDashboardFilters[filterKey] = [];
      }
    }

    this.setDefaultValuesAsActive(this.currentDashboardSpecificFilters);
  }

  mergePersistedGridLocationValuesToActiveDashboardFilters() {
    const cachedFilterModel = this.rptService.getCachedFilters();
    this.resetFilters();

    if (cachedFilterModel && Object.keys(cachedFilterModel).length) {
      for (const gridFilterKey in cachedFilterModel) {
        if (gridFilterKey && gridFilterKey.includes('location') && !gridFilterKey.includes('_1')) {
          const gridFilter: { values: string[] } = cachedFilterModel[gridFilterKey];

          if (gridFilter && gridFilter.values && Array.isArray(gridFilter.values) && gridFilter.values.length) {
            const locationActiveFilterValues: MultiselectType[] = this.activeDashboardFilters[gridFilterKey];

            if (locationActiveFilterValues && locationActiveFilterValues.length) {
              let transformedGridFilters = this.transformLocationFilterValues(
                gridFilter.values,
                locationActiveFilterValues.length + 1
              );

              transformedGridFilters = transformedGridFilters.filter((gridFilterValue: MultiselectType) => {
                return !this.existsValueInActiveFilters(locationActiveFilterValues, gridFilterValue);
              });

              this.activeDashboardFilters[gridFilterKey] = [...locationActiveFilterValues, ...transformedGridFilters];
            } else {
              this.activeDashboardFilters[gridFilterKey] = this.transformLocationFilterValues(gridFilter.values);
            }
          }
        }
      }
    }
  }

  public existsFilterInAvailableFilters(filterKey: string): boolean {
    return this.allAvailableDashboardFilters.some((filter: DashboardFilter) => filter.location === filterKey);
  }

  private existsValueInActiveFilters(activeFilters: MultiselectType[], value: MultiselectType): boolean {
    return (
      activeFilters.find((filter: MultiselectType) => {
        return filter.itemName === value.itemName;
      }) !== undefined
    );
  }

  private transformLocationFilterValues(locationValues: string[], startItemId: number = 0): MultiselectType[] {
    let multiselectValues: MultiselectType[] = [];
    startItemId = startItemId - 1;

    if (locationValues && locationValues.length) {
      multiselectValues = locationValues.map((loc: string, index: number) => {
        return {
          id: startItemId > -1 ? startItemId++ : index++,
          itemName: loc === null ? '(Blanks)' : loc,
        };
      });
    }

    return multiselectValues;
  }

  removeActiveDashboardSpecificFilters(): void {
    this.currentDashboardSpecificFilters.map((filter: DashboardFilter) => {
      delete this.activeDashboardFilters[filter.location];
    });
  }

  resetDashboardSpecificFilters(currentDashboardFilters: any[]): void {
    this.removeActiveDashboardSpecificFilters();
    this.setCurrentDashboardSpecificFilters(currentDashboardFilters);
  }

  private setCurrentDashboardSpecificFilters(currentDashboardFilters: any[]): void {
    const transformedSpecificFilters: DashboardFilter[] = currentDashboardFilters.map((filter: any) => {
      return {
        location: filter.filterId,
        locationName: filter.filterName,
        defaultValue: filter.defaultValue,
        locationValues: JSON.parse(filter.filterValues),
        transformedValues: this.transformDashboardSpecificFilterValues(filter.filterValues),
        dropwDownSettings: this.getDashboardSpecificFilterDropdowSettings(filter.singleSelect),
      };
    });

    this.currentDashboardSpecificFilters = transformedSpecificFilters;
    this.setDefaultValuesAsActive(this.currentDashboardSpecificFilters);
    this.allAvailableDashboardFilters = Object.assign(
      [],
      this.currentDashboardSpecificFilters,
      this.allAvailableDashboardFilters
    );
    this.triggerDashboardSpecificFiltersChange();
  }

  private setDefaultValuesAsActive(dashboardFilters: DashboardFilter[]): void {
    dashboardFilters.forEach((filter: DashboardFilter) => {
      if (filter.defaultValue) {
        this.activeDashboardFilters[filter.location] = [
          filter.transformedValues.find((item) => item.itemName?.toLowerCase() === filter.defaultValue?.toLowerCase()),
        ];
      }
    });
  }

  private getDashboardSpecificFilterDropdowSettings(isSingleSelection: string): any {
    let dropwDownSettings;

    if (isSingleSelection === 'TRUE') {
      dropwDownSettings = {
        singleSelection: true,
        enableSearchFilter: false,
        primaryKey: 'itemName',
        labelKey: 'translatedName',
      };
    } else {
      dropwDownSettings = {
        singleSelection: false,
        text: this.cService.readyTranslations.generic.select,
        selectAllText: this.cService.readyTranslations.generic.selectAll,
        unSelectAllText: this.cService.readyTranslations.generic.unselectAll,
        badgeShowLimit: 4,
        enableSearchFilter: true,
        primaryKey: 'itemName',
        labelKey: 'translatedName',
      };
    }

    return dropwDownSettings;
  }

  private transformDashboardSpecificFilterValues(jsonFilterValues: string): MultiselectType[] {
    let multiselectValues: MultiselectType[] = [];

    if (jsonFilterValues) {
      const filterValues: string[] = JSON.parse(jsonFilterValues);

      multiselectValues = filterValues.map((value: string, index: number) => {
        return { id: index++, itemName: value, translatedName: this.getDashboardFilterValueTranslation(value) };
      });
    }

    return multiselectValues;
  }

  private getDashboardFilterValueTranslation(filterValue: string): string {
    return this.cService.readyTranslations.dashboardFilters[filterValue]
      ? this.cService.readyTranslations.dashboardFilters[filterValue]
      : filterValue;
  }
}
